import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import {
  AccessTimeOutlined,
  Assignment,
  DescriptionOutlined,
  EventOutlined,
  KeyboardArrowUp,
  PersonOutline,
  RecordVoiceOverOutlined,
  SchoolOutlined,
  TrackChanges,
  WarningRounded,
} from '@material-ui/icons';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AttendanceData } from 'services/dataService';

const EMPTY_FORM = {
  contents_covered: '',
  main_learnings: '',
  practical_application: '',
  declaration_accepted: false,
};

const formatWorkload = minutes => {
  if (minutes == null || Number.isNaN(Number(minutes))) return '-';
  const totalMinutes = Number(minutes);
  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(remainingMinutes).padStart(2, '0')}`;
};

const formatPeriod = (startDate, endDate) => {
  const start = startDate ? moment(startDate).format('DD/MM/YYYY HH:mm') : '-';
  const end = endDate ? moment(endDate).format('DD/MM/YYYY HH:mm') : '-';
  return `${start} - ${end}`;
};

export default function LearningEvidence() {
  const classes = useStyles();
  const { t } = useTranslation(['form', 'button', 'toastify']);
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [selectedTrainingId, setSelectedTrainingId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const previousItemCount = useRef(0);

  const currentItem =
    items.find(item => item.training_id === selectedTrainingId) || items[0];
  const currentTrainingId = currentItem ? currentItem.training_id : null;
  const currentIndex = items.findIndex(
    item => item.training_id === currentTrainingId
  );
  const form = drafts[currentTrainingId] || EMPTY_FORM;

  const fetchPending = useCallback(async () => {
    try {
      const response = await AttendanceData.getPendingLearningEvidences();
      setItems(response.data.items || []);
    } catch (error) {
      toast.error(t('toastify:commons.toast_error_api'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending, location.pathname]);

  useEffect(() => {
    if (
      items.length > 0 &&
      !items.some(item => item.training_id === selectedTrainingId)
    ) {
      setSelectedTrainingId(items[0].training_id);
    }
  }, [items, selectedTrainingId]);

  useEffect(() => {
    if (previousItemCount.current === 0 && items.length > 0) {
      setMinimized(false);
    }
    previousItemCount.current = items.length;
  }, [items.length]);

  const canSubmit = useMemo(
    () =>
      form.contents_covered.trim() &&
      form.main_learnings.trim() &&
      form.practical_application.trim() &&
      form.declaration_accepted,
    [form]
  );

  const handleFieldChange = event => {
    const { name, value, checked, type } = event.target;
    setDrafts(current => ({
      ...current,
      [currentTrainingId]: {
        ...(current[currentTrainingId] || EMPTY_FORM),
        [name]: type === 'checkbox' ? checked : value,
      },
    }));
  };

  const handleSubmit = async () => {
    if (!currentItem || !canSubmit || submitting) return;

    setSubmitting(true);
    try {
      await AttendanceData.submitLearningEvidence(currentItem.training_id, form);
      const remainingItems = items.filter(
        item => item.training_id !== currentItem.training_id
      );
      setItems(remainingItems);
      setDrafts(current => {
        const updatedDrafts = { ...current };
        delete updatedDrafts[currentItem.training_id];
        return updatedDrafts;
      });
      setSelectedTrainingId(
        remainingItems.length > 0 ? remainingItems[0].training_id : null
      );
      setConfirmationOpen(false);
      setMinimized(false);
      toast.success(t('form:AttendanceEvidence.success'));
    } catch (error) {
      if (error.response && error.response.status === 409) {
        await fetchPending();
      }
      toast.error(t('toastify:commons.toast_error_api'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || items.length === 0) return null;

  return (
    <>
      <Dialog
        fullWidth
        maxWidth="lg"
        open={!minimized}
        onClose={() => setMinimized(true)}
        aria-labelledby="learning-evidence-title"
      >
        <DialogTitle id="learning-evidence-title">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <span>{t('form:AttendanceEvidence.title')}</span>
            <Typography variant="body2" color="textSecondary">
              {t('form:AttendanceEvidence.progress', {
                current: currentIndex + 1,
                total: items.length,
              })}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers className={classes.dialogContent}>
          <Grid container>
            <Grid item xs={12} md={4} className={classes.trainingListContainer}>
              <Box px={2} py={1.5}>
                <Typography variant="subtitle2">
                  {t('form:AttendanceEvidence.pendingTrainings')}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {t('form:AttendanceEvidence.selectTraining')}
                </Typography>
              </Box>
              <Divider />
              <List className={classes.trainingList} disablePadding>
                {items.map(item => {
                  const draft = drafts[item.training_id];
                  const hasDraft = !!draft && (
                    draft.contents_covered.trim() ||
                    draft.main_learnings.trim() ||
                    draft.practical_application.trim() ||
                    draft.declaration_accepted
                  );

                  return (
                    <ListItem
                      button
                      divider
                      key={item.training_id}
                      selected={item.training_id === currentTrainingId}
                      onClick={() => setSelectedTrainingId(item.training_id)}
                    >
                      <ListItemText
                        primary={item.training_name}
                        secondary={(
                          <>
                            {formatPeriod(item.start_date, item.end_date)}
                            {hasDraft && (
                              <Typography
                                component="span"
                                variant="caption"
                                className={classes.draftLabel}
                              >
                                {` • ${t('form:AttendanceEvidence.draft')}`}
                              </Typography>
                            )}
                          </>
                        )}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Grid>

            <Grid item xs={12} md={8} className={classes.formContainer}>
              <Paper elevation={0} className={classes.summary}>
                <div className={classes.summaryHeader}>
                  <Assignment className={classes.summaryHeaderIcon} />
                  <Typography variant="subtitle1" className={classes.summaryTitle}>
                    {t('form:AttendanceEvidence.trainingDetails')}
                  </Typography>
                </div>
                <Grid container spacing={2} className={classes.summaryGrid}>
                  <Info
                    icon={<PersonOutline />}
                    label={t('form:AttendanceEvidence.employee')}
                    value={currentItem.employee_name}
                    classes={classes}
                  />
                  <Info
                    icon={<SchoolOutlined />}
                    label={t('form:AttendanceEvidence.training')}
                    value={currentItem.training_name}
                    classes={classes}
                  />
                  <Info
                    fullWidth
                    icon={<DescriptionOutlined />}
                    label={t('form:AttendanceEvidence.description')}
                    value={currentItem.description}
                    classes={classes}
                  />
                  <Info
                    icon={<EventOutlined />}
                    label={t('form:AttendanceEvidence.period')}
                    value={formatPeriod(currentItem.start_date, currentItem.end_date)}
                    classes={classes}
                  />
                  <Info
                    icon={<AccessTimeOutlined />}
                    label={t('form:AttendanceEvidence.workload')}
                    value={formatWorkload(currentItem.workload)}
                    classes={classes}
                  />
                  <Info
                    icon={<RecordVoiceOverOutlined />}
                    label={t('form:AttendanceEvidence.instructor')}
                    value={currentItem.instructor}
                    classes={classes}
                  />
                </Grid>
              </Paper>

              <Box my={3} className={classes.objectiveCard}>
                <div className={classes.objectiveIcon}>
                  <TrackChanges />
                </div>
                <div>
                  <Typography variant="subtitle1" className={classes.objectiveTitle}>
                    {t('form:AttendanceEvidence.objectiveTitle')}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('form:AttendanceEvidence.objective')}
                  </Typography>
                </div>
              </Box>

              <Divider />

              <Box mt={3}>
                <EvidenceField
                  name="contents_covered"
                  label={t('form:AttendanceEvidence.contentsCovered')}
                  helperText={t('form:AttendanceEvidence.contentsCoveredHelp')}
                  value={form.contents_covered}
                  onChange={handleFieldChange}
                />
                <EvidenceField
                  name="main_learnings"
                  label={t('form:AttendanceEvidence.mainLearnings')}
                  helperText={t('form:AttendanceEvidence.mainLearningsHelp')}
                  value={form.main_learnings}
                  onChange={handleFieldChange}
                />
                <EvidenceField
                  name="practical_application"
                  label={t('form:AttendanceEvidence.practicalApplication')}
                  helperText={t('form:AttendanceEvidence.practicalApplicationHelp')}
                  value={form.practical_application}
                  onChange={handleFieldChange}
                />
              </Box>

              <Box mt={2}>
                <Typography variant="subtitle1" gutterBottom>
                  {t('form:AttendanceEvidence.declarationTitle')}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {t('form:AttendanceEvidence.declaration')}
                </Typography>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={form.declaration_accepted}
                      color="primary"
                      name="declaration_accepted"
                      onChange={handleFieldChange}
                    />
                  )}
                  label={t('form:AttendanceEvidence.declarationAccepted')}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMinimized(true)} color="primary">
            {t('form:AttendanceEvidence.minimize')}
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={!canSubmit || submitting}
            onClick={() => setConfirmationOpen(true)}
          >
            {t('form:AttendanceEvidence.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        fullWidth
        maxWidth="sm"
        open={confirmationOpen}
        onClose={() => !submitting && setConfirmationOpen(false)}
        aria-labelledby="learning-evidence-confirmation-title"
      >
        <DialogTitle id="learning-evidence-confirmation-title">
          <Box display="flex" alignItems="center">
            <WarningRounded className={classes.confirmationIcon} />
            {t('form:AttendanceEvidence.confirmationTitle')}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            {t('form:AttendanceEvidence.confirmationMessage')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            disabled={submitting}
            onClick={() => setConfirmationOpen(false)}
          >
            {t('form:AttendanceEvidence.confirmationCancel')}
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={submitting}
            onClick={handleSubmit}
          >
            {submitting && (
              <CircularProgress size={18} className={classes.progress} />
            )}
            {t('form:AttendanceEvidence.confirmationSubmit')}
          </Button>
        </DialogActions>
      </Dialog>

      {minimized && (
        <Paper elevation={8} className={classes.floatingPrompt}>
          <Assignment color="primary" className={classes.floatingIcon} />
          <Box flexGrow={1}>
            <Typography variant="subtitle2">
              {t('form:AttendanceEvidence.pendingTitle')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t('form:AttendanceEvidence.pendingCount', { count: items.length })}
            </Typography>
          </Box>
          <Button
            color="primary"
            startIcon={<KeyboardArrowUp />}
            onClick={() => setMinimized(false)}
          >
            {t('form:AttendanceEvidence.open')}
          </Button>
        </Paper>
      )}
    </>
  );
}

function Info({ icon, label, value, fullWidth, classes }) {
  return (
    <Grid item xs={12} sm={fullWidth ? 12 : 6}>
      <div className={classes.summaryItem}>
        <div className={classes.summaryItemIcon}>{icon}</div>
        <div className={classes.summaryItemContent}>
          <Typography className={classes.summaryItemLabel}>{label}</Typography>
          <Typography className={classes.summaryItemValue}>
            {value || '-'}
          </Typography>
        </div>
      </div>
    </Grid>
  );
}

function EvidenceField({ name, label, helperText, value, onChange }) {
  return (
    <Box mb={3}>
      <TextField
        fullWidth
        required
        multiline
        rows={4}
        name={name}
        label={label}
        helperText={helperText}
        value={value}
        variant="outlined"
        onChange={onChange}
      />
    </Box>
  );
}

const useStyles = makeStyles(theme => ({
  dialogContent: {
    padding: 0,
  },
  trainingListContainer: {
    borderRight: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
      borderRight: 0,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  trainingList: {
    maxHeight: '70vh',
    overflowY: 'auto',
    [theme.breakpoints.down('sm')]: {
      maxHeight: 220,
    },
  },
  formContainer: {
    maxHeight: '70vh',
    overflowY: 'auto',
    padding: theme.spacing(3),
  },
  draftLabel: {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  summary: {
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    backgroundColor: theme.palette.common.white,
    boxShadow: '0 6px 22px rgba(31, 45, 61, 0.06)',
  },
  summaryHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2.5),
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  summaryHeaderIcon: {
    marginRight: theme.spacing(1),
    fontSize: 22,
  },
  summaryTitle: {
    fontWeight: 600,
  },
  summaryGrid: {
    padding: theme.spacing(1, 1.5, 2),
  },
  summaryItem: {
    display: 'flex',
    alignItems: 'flex-start',
    minHeight: 68,
    height: '100%',
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: theme.palette.background.default,
  },
  summaryItemIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 34,
    height: 34,
    marginRight: theme.spacing(1.5),
    borderRadius: 8,
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.common.white,
    boxShadow: '0 2px 8px rgba(31, 45, 61, 0.08)',
    '& svg': {
      fontSize: 20,
    },
  },
  summaryItemContent: {
    minWidth: 0,
  },
  summaryItemLabel: {
    marginBottom: 2,
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 500,
  },
  summaryItemValue: {
    color: theme.palette.text.primary,
    fontSize: 15,
    lineHeight: 1.4,
    overflowWrap: 'anywhere',
  },
  objectiveCard: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: theme.spacing(2),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    borderRadius: 8,
    backgroundColor: theme.palette.action.hover,
  },
  objectiveIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 38,
    height: 38,
    marginRight: theme.spacing(1.5),
    borderRadius: '50%',
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    '& svg': {
      fontSize: 21,
    },
  },
  objectiveTitle: {
    marginBottom: theme.spacing(0.5),
    fontWeight: 600,
  },
  progress: {
    marginRight: theme.spacing(1),
    color: 'inherit',
  },
  confirmationIcon: {
    color: theme.palette.warning.main,
    marginRight: theme.spacing(1.5),
  },
  floatingPrompt: {
    position: 'fixed',
    right: theme.spacing(12),
    bottom: theme.spacing(3),
    zIndex: theme.zIndex.modal + 1,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    width: 390,
    maxWidth: `calc(100vw - ${theme.spacing(4)}px)`,
    padding: theme.spacing(2),
    borderLeft: `5px solid ${theme.palette.primary.main}`,
    [theme.breakpoints.down('xs')]: {
      right: theme.spacing(2),
    },
  },
  floatingIcon: {
    fontSize: 34,
  },
}));
